import React from "react";
import PropTypes from "prop-types";
import {
  Segment,
  Header,
  Icon,
  Form,
  Grid,
  Dropdown,
  Button,
  Message,
} from "semantic-ui-react";

const Reports = ({
  selectedDateRange,
  setSelectedDateRange,
  selectedFormat,
  setSelectedFormat,
  selectedDataPoints,
  setSelectedDataPoints,
  customDateRange,
  setCustomDateRange,
  handleExport,
  loading,
  overviewStats,
  searchTrends,
  frequentTerms,
  usageStats,
}) => {
  const dateRangeOptions = [
    { key: "1", text: "Today", value: "1" },
    { key: "7", text: "Last 7 days", value: "7" },
    { key: "30", text: "Last 30 days", value: "30" },
    { key: "90", text: "Last 90 days", value: "90" },
    { key: "180", text: "Last 6 months", value: "180" },
    { key: "365", text: "Last year", value: "365" },
  ];

  const formatOptions = [
    { key: "csv", text: "CSV", value: "csv", icon: "table" },
    { key: "xlsx", text: "Excel (XLSX)", value: "xlsx", icon: "file excel" },
    { key: "pdf", text: "PDF Report", value: "pdf", icon: "file pdf" },
  ];

  const dataPointOptions = [
    { key: "searches", text: "Search Overview Statistics", value: "searches" },
    {
      key: "frequent_terms",
      text: "Frequent Search Terms",
      value: "frequent_terms",
    },
    {
      key: "search_patterns",
      text: "Search Patterns & Trends",
      value: "search_patterns",
    },
    {
      key: "usage_analytics",
      text: "OPAC Usage Analytics",
      value: "usage_analytics",
    },
    {
      key: "user_engagement",
      text: "User Engagement Metrics",
      value: "user_engagement",
    },
    {
      key: "system_performance",
      text: "System Performance",
      value: "system_performance",
    },
  ];

  const clearDataPoints = () => {
    setSelectedDataPoints([]);
  };

  const downloadJSONFile = (data) => {
    const fileContent = JSON.stringify(data, null, 2);
    const blob = new Blob([fileContent], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `opac-analytics-report-${
      new Date().toISOString().split("T")[0]
    }.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadCSVFile = (data) => {
    const csvContent = convertToCSV(data);
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `opac-analytics-report-${
      new Date().toISOString().split("T")[0]
    }.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadXLSXFile = async (data) => {
    try {
      const XLSX = await import("xlsx");
      const workbook = XLSX.utils.book_new();

      const allData = [];

      // Add report header with date range
      allData.push(["OPAC ANALYTICS REPORT", "", "", "", ""]);
      allData.push(["Generated", new Date().toLocaleString(), "", "", ""]);

      const dateRangeLabel =
        dateRangeOptions.find(
          (option) => option.value === data.metadata.dateRange
        )?.text || `Last ${data.metadata.dateRange} days`;

      allData.push(["Date Range", dateRangeLabel, "", "", ""]);
      allData.push(["", "", "", "", ""]);

      // Overview Statistics
      if (data.data.overviewStats) {
        allData.push(["OVERVIEW STATISTICS", "", "", "", ""]);
        allData.push(["Metric", "Value", "Description", "", ""]);
        allData.push([
          "Total Searches",
          data.data.overviewStats.totalSearches,
          "All searches performed",
          "",
          "",
        ]);
        allData.push([
          "Successful Searches",
          data.data.overviewStats.successfulSearches,
          "Searches that returned results",
          "",
          "",
        ]);

        allData.push([
          "Success Rate",
          `${data.data.overviewStats.successRate}%`,
          "Percentage of successful searches",
          "",
          "",
        ]);
        allData.push([
          "Average Response Time",
          `${data.data.overviewStats.avgResponseTime}ms`,
          "Average search response time",
          "",
          "",
        ]);
        allData.push(["", "", "", "", ""]);
      }

      // Frequent Search Terms
      if (data.data.frequentTerms && data.data.frequentTerms.length > 0) {
        allData.push(["MOST FREQUENT SEARCH TERMS", "", "", "", ""]);
        allData.push([
          "Rank",
          "Search Term",
          "Total Searches",
          "Unique Users",
          "Percentage of Total",
        ]);

        const totalSearches = data.data.overviewStats
          ? data.data.overviewStats.totalSearches
          : data.data.frequentTerms.reduce((sum, term) => sum + term.count, 0);

        data.data.frequentTerms.forEach((term, index) => {
          const percentage =
            totalSearches > 0
              ? ((term.count / totalSearches) * 100).toFixed(1)
              : "0.0";
          allData.push([
            index + 1,
            term.term,
            term.count,
            term.uniqueUsers || 0,
            `${percentage}%`,
          ]);
        });
        allData.push(["", "", "", "", ""]);
      }

      // Search Trends
      if (data.data.searchTrends && data.data.searchTrends.length > 0) {
        allData.push(["DAILY SEARCH TRENDS", "", "", "", ""]);
        allData.push([
          "Date",
          "Total Searches",
          "Successful",
          "Failed",
          "Success Rate",
        ]);

        data.data.searchTrends.forEach((trend) => {
          const successRate =
            trend.totalSearches > 0
              ? ((trend.successful / trend.totalSearches) * 100).toFixed(1)
              : "0.0";
          allData.push([
            trend.date,
            trend.totalSearches,
            trend.successful,
            trend.failed,
            `${successRate}%`,
          ]);
        });
        allData.push(["", "", "", "", ""]);
      }

      // Usage Analytics Summary
      if (data.data.usageAnalytics) {
        allData.push(["USAGE ANALYTICS SUMMARY", "", "", "", ""]);
        allData.push(["Metric", "Value", "Description", "", ""]);
        allData.push([
          "Active Users",
          data.data.usageAnalytics.activeUsers,
          "Total unique users in period",
          "",
          "",
        ]);
        allData.push([
          "Total Sessions",
          data.data.usageAnalytics.totalSessions,
          "Total user sessions",
          "",
          "",
        ]);
        allData.push([
          "Average Session Duration",
          `${data.data.usageAnalytics.avgSessionDuration} minutes`,
          "Average time per session",
          "",
          "",
        ]);
        allData.push(["", "", "", "", ""]);

        // Peak Usage Hours
        if (data.data.usageAnalytics.peakUsageHours?.length > 0) {
          allData.push(["PEAK USAGE HOURS", "", "", "", ""]);
          allData.push([
            "Hour",
            "Total Searches",
            "Unique Users",
            "Percentage of Daily Traffic",
            "",
          ]);
          data.data.usageAnalytics.peakUsageHours.forEach((hour) => {
            allData.push([
              hour.hour,
              hour.searches,
              hour.uniqueUsers || 0,
              `${hour.percentage}%`,
              "",
            ]);
          });
          allData.push(["", "", "", "", ""]);
        }
      }

      // User Engagement
      if (data.data.userEngagement) {
        allData.push(["USER ENGAGEMENT METRICS", "", "", "", ""]);
        const totalUsers =
          data.data.userEngagement.newUsers +
          data.data.userEngagement.returningUsers;
        const newUserPercentage =
          totalUsers > 0
            ? ((data.data.userEngagement.newUsers / totalUsers) * 100).toFixed(
                1
              )
            : "0.0";
        const returningUserPercentage =
          totalUsers > 0
            ? (
                (data.data.userEngagement.returningUsers / totalUsers) *
                100
              ).toFixed(1)
            : "0.0";

        allData.push(["Metric", "Count", "Percentage", "Description", ""]);
        allData.push([
          "New Users",
          data.data.userEngagement.newUsers,
          `${newUserPercentage}%`,
          "First-time users in period",
          "",
        ]);
        allData.push([
          "Returning Users",
          data.data.userEngagement.returningUsers,
          `${returningUserPercentage}%`,
          "Users who returned",
          "",
        ]);
        allData.push([
          "Bounce Rate",
          "",
          `${data.data.userEngagement.bounceRate}%`,
          "Users with only one search",
          "",
        ]);
        allData.push(["", "", "", "", ""]);
      }

      // System Performance
      if (data.data.systemPerformance) {
        allData.push(["SYSTEM PERFORMANCE METRICS", "", "", "", ""]);
        allData.push(["Metric", "Value", "Status", "Description", ""]);

        const loadTimeStatus =
          data.data.systemPerformance.avgLoadTime < 1000
            ? "Good"
            : data.data.systemPerformance.avgLoadTime < 2000
            ? "Fair"
            : "Poor";
        const uptimeStatus =
          data.data.systemPerformance.uptime >= 99
            ? "Excellent"
            : data.data.systemPerformance.uptime >= 95
            ? "Good"
            : "Poor";
        const errorStatus =
          data.data.systemPerformance.errorRate < 1
            ? "Excellent"
            : data.data.systemPerformance.errorRate < 5
            ? "Good"
            : "Poor";

        allData.push([
          "Average Load Time",
          `${data.data.systemPerformance.avgLoadTime}ms`,
          loadTimeStatus,
          "Average search response time",
          "",
        ]);
        allData.push([
          "System Uptime",
          `${data.data.systemPerformance.uptime}%`,
          uptimeStatus,
          "System availability percentage",
          "",
        ]);
        allData.push([
          "Error Rate",
          `${data.data.systemPerformance.errorRate}%`,
          errorStatus,
          "Percentage of failed requests",
          "",
        ]);
      }

      const worksheet = XLSX.utils.aoa_to_sheet(allData);

      worksheet["!cols"] = [
        { width: 25 },
        { width: 20 },
        { width: 30 },
        { width: 15 },
        { width: 20 },
      ];

      XLSX.utils.book_append_sheet(workbook, worksheet, "OPAC Analytics");

      XLSX.writeFile(
        workbook,
        `opac-analytics-report-${new Date().toISOString().split("T")[0]}.xlsx`
      );
    } catch (error) {
      console.error("XLSX generation failed:", error);
      alert(
        "Excel library not available. Please install xlsx: npm install xlsx"
      );
    }
  };

  const downloadPDFFile = async (data) => {
    try {
      const { jsPDF } = await import("jspdf");
      const doc = new jsPDF();

      const leftLogoUrl = `${process.env.PUBLIC_URL}/images/university-logo.png`;
      const rightLogoUrl = `${process.env.PUBLIC_URL}/images/lrc-logo.png`;

      let yPos = 35;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 20;

      const addHeader = async () => {
        try {
          const leftImg = await loadImage(leftLogoUrl);
          doc.addImage(leftImg, "PNG", 15, 10, 25, 25);

          const rightImg = await loadImage(rightLogoUrl);
          doc.addImage(rightImg, "PNG", 170, 10, 25, 25);

          doc.setFontSize(16);
          doc.setFont(undefined, "bold");
          const title = "OPAC ANALYTICS REPORT";
          const titleWidth = doc.getTextWidth(title);
          const pageWidth = doc.internal.pageSize.width;
          doc.text(title, (pageWidth - titleWidth) / 2, 20);

          doc.setFontSize(10);
          doc.setFont(undefined, "normal");

          const dateRangeLabel =
            dateRangeOptions.find(
              (option) => option.value === data.metadata.dateRange
            )?.text || `Last ${data.metadata.dateRange} days`;

          const subtitle = `Generated on ${new Date().toLocaleDateString()} | ${dateRangeLabel}`;
          const subtitleWidth = doc.getTextWidth(subtitle);
          doc.text(subtitle, (pageWidth - subtitleWidth) / 2, 27);

          yPos = 45;
        } catch (error) {
          console.error("Error loading logos:", error);
          doc.setFontSize(16);
          doc.setFont(undefined, "bold");
          const title = "OPAC ANALYTICS REPORT";
          const titleWidth = doc.getTextWidth(title);
          const pageWidth = doc.internal.pageSize.width;
          doc.text(title, (pageWidth - titleWidth) / 2, 20);

          doc.setFontSize(10);
          doc.setFont(undefined, "normal");

          const dateRangeLabel =
            dateRangeOptions.find(
              (option) => option.value === data.metadata.dateRange
            )?.text || `Last ${data.metadata.dateRange} days`;

          const subtitle = `Generated on ${new Date().toLocaleDateString()} | ${dateRangeLabel}`;
          const subtitleWidth = doc.getTextWidth(subtitle);
          doc.text(subtitle, (pageWidth - subtitleWidth) / 2, 27);
          yPos = 35;
        }
      };

      const loadImage = (url) => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = "Anonymous";
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = url;
        });
      };

      const checkNewPage = async (requiredSpace = 20) => {
        if (yPos + requiredSpace > pageHeight - margin) {
          doc.addPage();
          await addHeader();
        }
      };

      const splitTextToFit = (text, maxWidth) => {
        const words = String(text || "").split(" ");
        const lines = [];
        let currentLine = "";

        for (const word of words) {
          const testLine = currentLine ? `${currentLine} ${word}` : word;
          const textWidth = doc.getTextWidth(testLine);

          if (textWidth <= maxWidth) {
            currentLine = testLine;
          } else {
            if (currentLine) {
              lines.push(currentLine);
              currentLine = word;
            } else {
              lines.push(word);
            }
          }
        }

        if (currentLine) {
          lines.push(currentLine);
        }

        return lines;
      };

      const drawTable = async (title, headers, rows, proportions) => {
        await checkNewPage(30);

        const pageWidth = doc.internal.pageSize.width;
        const totalWidth = pageWidth - 2 * margin;
        const widths = proportions.map(
          (p) => (p / proportions.reduce((a, b) => a + b, 0)) * totalWidth
        );

        // --- Title ---
        doc.setFontSize(14).setFont(undefined, "bold");
        doc.text(title, margin, yPos);
        yPos += 10;

        // --- Headers ---
        doc.setFontSize(10);
        let x = margin;
        headers.forEach((h, i) => {
          doc.text(h, x, yPos);
          x += widths[i];
        });
        yPos += 11;

        doc.line(margin, yPos - 5, margin + totalWidth, yPos - 5);

        // --- Rows ---
        doc.setFont(undefined, "normal");
        for (const row of rows) {
          const cellLines = row.map((c, i) => splitTextToFit(c, widths[i] - 2));
          const rowHeight = Math.max(...cellLines.map((l) => l.length)) * 6;
          await checkNewPage(rowHeight + 5);

          x = margin;
          cellLines.forEach((lines, i) => {
            lines.forEach((ln, li) => doc.text(ln, x, yPos + li * 6));
            x += widths[i];
          });
          yPos += rowHeight + 5;
        }
        yPos += 5;
      };

      await addHeader();

      if (data.data.overviewStats) {
        const headers = ["Metric", "Value", "Description"];
        const rows = [
          [
            "Total Searches",
            data.data.overviewStats.totalSearches.toLocaleString(),
            "All searches performed",
          ],
          [
            "Successful Searches",
            data.data.overviewStats.successfulSearches.toLocaleString(),
            "Searches that returned results",
          ],
          [
            "Success Rate",
            `${data.data.overviewStats.successRate}%`,
            "Percentage of successful searches",
          ],
          [
            "Avg Response Time",
            `${data.data.overviewStats.avgResponseTime}ms`,
            "Average search response time",
          ],
        ];
        const columnWidths = [50, 40, 80];
        await drawTable("OVERVIEW STATISTICS", headers, rows, columnWidths);
      }

      if (data.data.frequentTerms && data.data.frequentTerms.length > 0) {
        const headers = ["Rank", "Search Term", "Count", "Users", "%"];
        const totalSearches = data.data.overviewStats
          ? data.data.overviewStats.totalSearches
          : data.data.frequentTerms.reduce((sum, term) => sum + term.count, 0);

        const rows = data.data.frequentTerms.slice(0, 15).map((term, index) => {
          const percentage =
            totalSearches > 0
              ? ((term.count / totalSearches) * 100).toFixed(1)
              : "0.0";
          return [
            (index + 1).toString(),
            term.term,
            term.count.toString(),
            (term.uniqueUsers || 0).toString(),
            `${percentage}%`,
          ];
        });
        const columnWidths = [15, 60, 25, 25, 20];
        await drawTable(
          "MOST FREQUENT SEARCH TERMS",
          headers,
          rows,
          columnWidths
        );
      }

      if (data.data.searchTrends && data.data.searchTrends.length > 0) {
        const headers = ["Date", "Total", "Success", "Failed", "Rate"];
        const rows = data.data.searchTrends.slice(-10).map((trend) => {
          const successRate =
            trend.totalSearches > 0
              ? ((trend.successful / trend.totalSearches) * 100).toFixed(1)
              : "0.0";
          return [
            new Date(trend.date).toLocaleDateString(),
            trend.totalSearches.toString(),
            trend.successful.toString(),
            trend.failed.toString(),
            `${successRate}%`,
          ];
        });
        const columnWidths = [35, 25, 25, 25, 25];
        await drawTable(
          "DAILY SEARCH TRENDS (Last 10 Days)",
          headers,
          rows,
          columnWidths
        );
      }

      if (data.data.usageAnalytics) {
        const headers = ["Metric", "Value", "Description"];
        const rows = [
          [
            "Active Users",
            data.data.usageAnalytics.activeUsers.toLocaleString(),
            "Total unique users in period",
          ],
          [
            "Total Sessions",
            data.data.usageAnalytics.totalSessions.toLocaleString(),
            "Total user sessions",
          ],
          [
            "Avg Session Duration",
            `${data.data.usageAnalytics.avgSessionDuration} min`,
            "Average time per session",
          ],
        ];
        const columnWidths = [50, 40, 80];
        await drawTable("USAGE ANALYTICS SUMMARY", headers, rows, columnWidths);

        if (data.data.usageAnalytics.peakUsageHours?.length > 0) {
          const headers = ["Hour", "Searches", "Users", "Traffic %"];
          const rows = data.data.usageAnalytics.peakUsageHours
            .slice(0, 6)
            .map((hour) => [
              hour.hour,
              hour.searches.toString(),
              (hour.uniqueUsers || 0).toString(),
              `${hour.percentage}%`,
            ]);
          const columnWidths = [30, 30, 30, 30];
          await drawTable("PEAK USAGE HOURS", headers, rows, columnWidths);
        }
      }

      if (data.data.userEngagement) {
        const totalUsers =
          data.data.userEngagement.newUsers +
          data.data.userEngagement.returningUsers;
        const newUserPercentage =
          totalUsers > 0
            ? ((data.data.userEngagement.newUsers / totalUsers) * 100).toFixed(
                1
              )
            : "0.0";
        const returningUserPercentage =
          totalUsers > 0
            ? (
                (data.data.userEngagement.returningUsers / totalUsers) *
                100
              ).toFixed(1)
            : "0.0";

        const headers = ["Metric", "Count", "Percentage", "Description"];
        const rows = [
          [
            "New Users",
            data.data.userEngagement.newUsers.toString(),
            `${newUserPercentage}%`,
            "First-time users",
          ],
          [
            "Returning Users",
            data.data.userEngagement.returningUsers.toString(),
            `${returningUserPercentage}%`,
            "Users who returned",
          ],
          [
            "Bounce Rate",
            "",
            `${data.data.userEngagement.bounceRate}%`,
            "Single-search users",
          ],
        ];
        const columnWidths = [40, 25, 25, 60];
        await drawTable("USER ENGAGEMENT METRICS", headers, rows, columnWidths);
      }

      if (data.data.systemPerformance) {
        const loadTimeStatus =
          data.data.systemPerformance.avgLoadTime < 1000
            ? "Good"
            : data.data.systemPerformance.avgLoadTime < 2000
            ? "Fair"
            : "Poor";
        const uptimeStatus =
          data.data.systemPerformance.uptime >= 99
            ? "Excellent"
            : data.data.systemPerformance.uptime >= 95
            ? "Good"
            : "Poor";
        const errorStatus =
          data.data.systemPerformance.errorRate < 1
            ? "Excellent"
            : data.data.systemPerformance.errorRate < 5
            ? "Good"
            : "Poor";

        const headers = ["Metric", "Value", "Status", "Description"];
        const rows = [
          [
            "Avg Load Time",
            `${data.data.systemPerformance.avgLoadTime}ms`,
            loadTimeStatus,
            "Search response time",
          ],
          [
            "System Uptime",
            `${data.data.systemPerformance.uptime}%`,
            uptimeStatus,
            "System availability",
          ],
          [
            "Error Rate",
            `${data.data.systemPerformance.errorRate}%`,
            errorStatus,
            "Failed requests %",
          ],
        ];
        const columnWidths = [40, 30, 25, 55];
        await drawTable(
          "SYSTEM PERFORMANCE METRICS",
          headers,
          rows,
          columnWidths
        );
      }

      doc.save(
        `opac-analytics-report-${new Date().toISOString().split("T")[0]}.pdf`
      );
    } catch (error) {
      console.error("PDF generation failed:", error);
      alert(
        "PDF library not available. Please install jsPDF: npm install jspdf"
      );
    }
  };

  const convertToCSV = (data) => {
    const csvRows = [];

    // Add report header with date range label
    csvRows.push(["OPAC ANALYTICS REPORT", "", "", "", ""]);
    csvRows.push(["Generated", new Date().toLocaleString(), "", "", ""]);

    const dateRangeLabel =
      dateRangeOptions.find(
        (option) => option.value === data.metadata.dateRange
      )?.text || `Last ${data.metadata.dateRange} days`;

    csvRows.push(["Date Range", dateRangeLabel, "", "", ""]);
    csvRows.push(["", "", "", "", ""]);

    if (data.data.overviewStats) {
      csvRows.push(["OVERVIEW STATISTICS", "", "", "", ""]);
      csvRows.push(["Metric", "Value", "Description", "", ""]);
      csvRows.push([
        "Total Searches",
        data.data.overviewStats.totalSearches,
        "All searches performed",
        "",
        "",
      ]);
      csvRows.push([
        "Successful Searches",
        data.data.overviewStats.successfulSearches,
        "Searches that returned results",
        "",
        "",
      ]);

      csvRows.push([
        "Success Rate",
        `${data.data.overviewStats.successRate}%`,
        "Percentage of successful searches",
        "",
        "",
      ]);
      csvRows.push([
        "Average Response Time",
        `${data.data.overviewStats.avgResponseTime}ms`,
        "Average search response time",
        "",
        "",
      ]);
      csvRows.push(["", "", "", "", ""]);
    }

    if (data.data.frequentTerms && data.data.frequentTerms.length > 0) {
      csvRows.push(["MOST FREQUENT SEARCH TERMS", "", "", "", ""]);
      csvRows.push([
        "Rank",
        "Search Term",
        "Total Searches",
        "Unique Users",
        "Percentage of Total",
      ]);

      const totalSearches = data.data.overviewStats
        ? data.data.overviewStats.totalSearches
        : data.data.frequentTerms.reduce((sum, term) => sum + term.count, 0);

      data.data.frequentTerms.forEach((term, index) => {
        const percentage =
          totalSearches > 0
            ? ((term.count / totalSearches) * 100).toFixed(1)
            : "0.0";
        csvRows.push([
          index + 1,
          term.term,
          term.count,
          term.uniqueUsers || 0,
          `${percentage}%`,
        ]);
      });
      csvRows.push(["", "", "", "", ""]);
    }

    if (data.data.searchTrends && data.data.searchTrends.length > 0) {
      csvRows.push(["DAILY SEARCH TRENDS", "", "", "", ""]);
      csvRows.push([
        "Date",
        "Total Searches",
        "Successful",
        "Failed",
        "Success Rate",
      ]);
      data.data.searchTrends.forEach((trend) => {
        const successRate =
          trend.totalSearches > 0
            ? ((trend.successful / trend.totalSearches) * 100).toFixed(1)
            : "0.0";
        csvRows.push([
          trend.date,
          trend.totalSearches,
          trend.successful,
          trend.failed,
          `${successRate}%`,
        ]);
      });
      csvRows.push(["", "", "", "", ""]);
    }

    if (data.data.usageAnalytics) {
      csvRows.push(["USAGE ANALYTICS SUMMARY", "", "", "", ""]);
      csvRows.push(["Metric", "Value", "Description", "", ""]);
      csvRows.push([
        "Active Users",
        data.data.usageAnalytics.activeUsers,
        "Total unique users in period",
        "",
        "",
      ]);
      csvRows.push([
        "Total Sessions",
        data.data.usageAnalytics.totalSessions,
        "Total user sessions",
        "",
        "",
      ]);
      csvRows.push([
        "Average Session Duration",
        `${data.data.usageAnalytics.avgSessionDuration} minutes`,
        "Average time per session",
        "",
        "",
      ]);
      csvRows.push(["", "", "", "", ""]);

      if (data.data.usageAnalytics.peakUsageHours?.length > 0) {
        csvRows.push(["PEAK USAGE HOURS", "", "", "", ""]);
        csvRows.push([
          "Hour",
          "Total Searches",
          "Unique Users",
          "Percentage of Daily Traffic",
          "",
        ]);
        data.data.usageAnalytics.peakUsageHours.forEach((hour) => {
          csvRows.push([
            hour.hour,
            hour.searches,
            hour.uniqueUsers || 0,
            `${hour.percentage}%`,
            "",
          ]);
        });
        csvRows.push(["", "", "", "", ""]);
      }
    }

    if (data.data.userEngagement) {
      csvRows.push(["USER ENGAGEMENT METRICS", "", "", "", ""]);
      csvRows.push(["Metric", "Count", "Percentage", "Description", ""]);
      const totalUsers =
        data.data.userEngagement.newUsers +
        data.data.userEngagement.returningUsers;
      const newUserPercentage =
        totalUsers > 0
          ? ((data.data.userEngagement.newUsers / totalUsers) * 100).toFixed(1)
          : "0.0";
      const returningUserPercentage =
        totalUsers > 0
          ? (
              (data.data.userEngagement.returningUsers / totalUsers) *
              100
            ).toFixed(1)
          : "0.0";

      csvRows.push([
        "New Users",
        data.data.userEngagement.newUsers,
        `${newUserPercentage}%`,
        "First-time users in period",
        "",
      ]);
      csvRows.push([
        "Returning Users",
        data.data.userEngagement.returningUsers,
        `${returningUserPercentage}%`,
        "Users who returned",
        "",
      ]);
      csvRows.push([
        "Bounce Rate",
        "",
        `${data.data.userEngagement.bounceRate}%`,
        "Users with only one search",
        "",
      ]);
      csvRows.push(["", "", "", "", ""]);
    }

    if (data.data.systemPerformance) {
      csvRows.push(["SYSTEM PERFORMANCE METRICS", "", "", "", ""]);
      csvRows.push(["Metric", "Value", "Status", "Description", ""]);

      const loadTimeStatus =
        data.data.systemPerformance.avgLoadTime < 1000
          ? "Good"
          : data.data.systemPerformance.avgLoadTime < 2000
          ? "Fair"
          : "Poor";
      const uptimeStatus =
        data.data.systemPerformance.uptime >= 99
          ? "Excellent"
          : data.data.systemPerformance.uptime >= 95
          ? "Good"
          : "Poor";
      const errorStatus =
        data.data.systemPerformance.errorRate < 1
          ? "Excellent"
          : data.data.systemPerformance.errorRate < 5
          ? "Good"
          : "Poor";

      csvRows.push([
        "Average Load Time",
        `${data.data.systemPerformance.avgLoadTime}ms`,
        loadTimeStatus,
        "Average search response time",
        "",
      ]);
      csvRows.push([
        "System Uptime",
        `${data.data.systemPerformance.uptime}%`,
        uptimeStatus,
        "System availability percentage",
        "",
      ]);
      csvRows.push([
        "Error Rate",
        `${data.data.systemPerformance.errorRate}%`,
        errorStatus,
        "Percentage of failed requests",
        "",
      ]);
    }

    return csvRows
      .map((row) =>
        row
          .map((cell) => {
            const cellStr = String(cell || "");
            if (
              cellStr.includes(",") ||
              cellStr.includes('"') ||
              cellStr.includes("\n")
            ) {
              return `"${cellStr.replace(/"/g, '""')}"`;
            }
            return cellStr;
          })
          .join(",")
      )
      .join("\n");
  };

  const handleCustomExport = async () => {
    try {
      const exportData = {
        metadata: {
          dateRange: selectedDateRange,
          customDateRange:
            selectedDateRange === "custom" ? customDateRange : null,
          generatedAt: new Date().toISOString(),
          format: selectedFormat,
          dataPoints: selectedDataPoints,
          systemWide: true,
        },
        data: {},
      };

      if (selectedDataPoints.includes("searches") && overviewStats) {
        exportData.data.overviewStats = {
          totalSearches: overviewStats.totalSearches,
          successfulSearches: overviewStats.successfulSearches,
          failedSearches: overviewStats.failedSearches,
          successRate: overviewStats.successRate,
          avgResponseTime: overviewStats.avgResponseTime,
        };
      }

      if (selectedDataPoints.includes("frequent_terms")) {
        if (frequentTerms) {
          exportData.data.frequentTerms = frequentTerms.map((term) => ({
            term: term.term,
            count: term.count,
            uniqueUsers: term.uniqueUsers || 0,
          }));
        }
      }

      if (selectedDataPoints.includes("search_patterns") && searchTrends) {
        exportData.data.searchTrends = searchTrends.map((trend) => ({
          date: trend.date,
          totalSearches: trend.total,
          successful: trend.successful || 0,
          failed: trend.failed || 0,
          uniqueUsers: trend.uniqueUsers || 0,
        }));
      }

      if (selectedDataPoints.includes("usage_analytics") && usageStats) {
        exportData.data.usageAnalytics = {
          activeUsers: usageStats.activeUsers,
          totalSessions: usageStats.totalSessions,
          avgSessionDuration: usageStats.avgSessionDuration,
          peakUsageHours: usageStats.peakUsageHours,
          topCategories: usageStats.topCategories,
        };
      }

      if (
        selectedDataPoints.includes("user_engagement") &&
        usageStats?.userEngagement
      ) {
        exportData.data.userEngagement = {
          newUsers: usageStats.userEngagement.newUsers,
          returningUsers: usageStats.userEngagement.returningUsers,
          bounceRate: usageStats.userEngagement.bounceRate,
        };
      }

      if (
        selectedDataPoints.includes("system_performance") &&
        usageStats?.systemPerformance
      ) {
        exportData.data.systemPerformance = {
          avgLoadTime: usageStats.systemPerformance.avgLoadTime,
          uptime: usageStats.systemPerformance.uptime,
          errorRate: usageStats.systemPerformance.errorRate,
        };
      }

      switch (selectedFormat) {
        case "json":
          downloadJSONFile(exportData);
          break;
        case "csv":
          downloadCSVFile(exportData);
          break;
        case "xlsx":
          await downloadXLSXFile(exportData);
          break;
        case "pdf":
          await downloadPDFFile(exportData);
          break;
        default:
          downloadJSONFile(exportData);
      }
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  return (
    <Segment>
      <Header as="h3" style={{ color: "#225745" }}>
        <Icon name="file alternate" />
        Generate Custom Reports
      </Header>
      <style jsx="true">
        {`
          .ui.dropdown .ui.label {
            background-color: #f4b952 !important;
            color: black !important;
          }
          .ui.dropdown .ui.label:before {
            border-color: transparent transparent transparent #225745 !important;
          }
          .ui.dropdown .ui.label .delete.icon {
            color: black !important;
          }
        `}
      </style>
      <Form>
        <Grid>
          <Grid.Row>
            <Grid.Column width={8}>
              <Form.Field>
                <label>Date Range</label>
                <Dropdown
                  selection
                  options={dateRangeOptions}
                  value={selectedDateRange}
                  onChange={(e, { value }) => setSelectedDateRange(value)}
                />
              </Form.Field>
              {selectedDateRange === "custom" && (
                <Form.Group widths="equal">
                  <Form.Input
                    type="date"
                    label="From Date"
                    value={customDateRange.fromDate}
                    onChange={(e, { value }) =>
                      setCustomDateRange({
                        ...customDateRange,
                        fromDate: value,
                      })
                    }
                  />
                  <Form.Input
                    type="date"
                    label="To Date"
                    value={customDateRange.toDate}
                    onChange={(e, { value }) =>
                      setCustomDateRange({ ...customDateRange, toDate: value })
                    }
                  />
                </Form.Group>
              )}
            </Grid.Column>
            <Grid.Column width={8}>
              <Form.Field>
                <label>Export Format</label>
                <Dropdown
                  selection
                  options={formatOptions}
                  value={selectedFormat}
                  onChange={(e, { value }) => setSelectedFormat(value)}
                />
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={16}>
              <Form.Field>
                <label
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  Select Data Points to Include
                  <Button
                    type="button"
                    size="mini"
                    color="red"
                    icon
                    onClick={clearDataPoints}
                    disabled={selectedDataPoints.length === 0}
                    style={{ marginLeft: "auto" }}
                    title="Clear all selections"
                  >
                    <Icon name="close" />
                  </Button>
                </label>
                <Dropdown
                  multiple
                  selection
                  options={dataPointOptions}
                  value={selectedDataPoints}
                  onChange={(e, { value }) => setSelectedDataPoints(value)}
                  placeholder="Choose data points to include in the report"
                />
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={16}>
              <Button
                primary
                size="large"
                onClick={handleCustomExport}
                loading={loading}
                disabled={selectedDataPoints.length === 0}
              >
                <Icon name="download" />
                Generate & Download Report
              </Button>
              <Message
                info
                size="small"
                content="Select at least one data point to generate a report. The report will include data for the specified date range."
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Form>
    </Segment>
  );
};

Reports.displayName = "Reports";

Reports.propTypes = {
  selectedDateRange: PropTypes.string.isRequired,
  setSelectedDateRange: PropTypes.func.isRequired,
  selectedFormat: PropTypes.string.isRequired,
  setSelectedFormat: PropTypes.func.isRequired,
  selectedDataPoints: PropTypes.arrayOf(PropTypes.string).isRequired,
  setSelectedDataPoints: PropTypes.func.isRequired,
  customDateRange: PropTypes.shape({
    fromDate: PropTypes.string.isRequired,
    toDate: PropTypes.string.isRequired,
  }).isRequired,
  setCustomDateRange: PropTypes.func.isRequired,
  handleExport: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  overviewStats: PropTypes.object,
  searchTrends: PropTypes.array,
  frequentTerms: PropTypes.array,
  usageStats: PropTypes.object,
};

export default Reports;
